// ============================================================
// Add Student Wizard Component
// ============================================================

import { useState, useEffect } from 'react';
import { Box, Stepper, Step, StepLabel, Button, Typography, CircularProgress, Avatar, Grid, Divider, Paper, Chip } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { tokens } from '@/theme/tokens';
import { useCreateStudent } from '@/hooks/useCreateStudent';
import { useUpdateStudent } from '@/hooks/useUpdateStudent';
import studentService from '@/services/studentService';
import academicSessionService from '@/services/academicSessionService';
import { useSchool } from '@/hooks/useSchool';
import { studentsApi } from '@/api/students.api';

// Steps
import { StepBasicDetails } from './StepBasicDetails';
import { StepAcademic } from './StepAcademic';
import { StepParents } from './StepParents';

import { StepAddress } from './StepAddress';
import { StepPreviousAcademicsAndDocuments, normalizeDocTypeKey } from './StepPreviousAcademicsAndDocuments';

import { CalendarX, Plus } from 'lucide-react';

const steps = ['Basic & Academic Info', 'Parent & Address Details', 'Documents & Review'];

const initialValues = {
  // Step 1 - Basic Details
  sr_number: '',
  first_name: '',
  middle_name: '',
  last_name: '',
  gender: '',
  date_of_birth: '',
  blood_group: '',
  aadhaar_no: '',
  religion: '',
  category: '',
  caste: '',
  nationality: 'INDIAN',
  height: '',
  weight: '',
  is_rte: false,
  photo: '',

  // Step 2 - Academic Info
  academic_session_id: '',
  class_id: '',
  section_id: '',
  roll_number: '',
  auto_roll_number: true,
  admission_date: new Date().toISOString().split('T')[0],
  admission_type: 'New',
  admission_test_marks: '',
  remarks: '',

  // Step 3 - Parent Info
  father_name: '',
  father_mobile: '',
  father_email: '',
  father_occupation: '',
  mother_name: '',
  mother_mobile: '',
  mother_email: '',
  mother_occupation: '',
  guardian_name: '',
  guardian_relation: '',
  guardian_mobile: '',
  annual_income: '',
  primary_login: 'FATHER',

  // Step 4 - Address Details
  sameAsCurrent: false,
  current_address: '',
  current_city: '',
  current_state: '',
  current_country: 'India',
  current_pincode: '',
  permanent_address: '',
  permanent_city: '',
  permanent_state: '',
  permanent_country: 'India',
  permanent_pincode: '',

  // New section: Academic History & Documents
  previous_academics: [],
  documents: [],
  siblings: [],
};

const validationSchemas = [
  // Step 1 - Basic & Academic Details
  Yup.object().shape({
    sr_number: Yup.string()
      .trim()
      .nullable(),
    first_name: Yup.string()
      .trim()
      .required('Required')
      .matches(/^[a-zA-Z\s.,'()/+-]+$/, 'First name must contain only letters'),
    last_name: Yup.string()
      .trim()
      .required('Required')
      .matches(/^[a-zA-Z\s.,'()/+-]+$/, 'Last name must contain only letters'),
    gender: Yup.string().required('Required'),
    date_of_birth: Yup.string().required('Date of birth is required'),
    aadhaar_no: Yup.string()
      .trim()
      .nullable()
      .test('is-aadhaar', 'Aadhaar number must be exactly 12 digits', (val) => !val || /^\d{12}$/.test(val)),
    religion: Yup.string()
      .trim()
      .nullable()
      .test('is-religion', 'Religion must contain only letters', (val) => !val || /^[a-zA-Z\s.,'()/+-]+$/.test(val)),
    category: Yup.string()
      .trim()
      .nullable(),
    caste: Yup.string()
      .trim()
      .nullable()
      .test('is-valid-caste', 'Caste must contain only letters', (val) => !val || /^[a-zA-Z\s.,'()/+-]+$/.test(val)),
    height: Yup.string().nullable().test('is-num', 'Height must be a valid number', (val) => !val || /^\d+(\.\d{1,2})?$/.test(String(val))),
    weight: Yup.string().nullable().test('is-num', 'Weight must be a valid number', (val) => !val || /^\d+(\.\d{1,2})?$/.test(String(val))),
    academic_session_id: Yup.string().required('Required'),
    class_id: Yup.string().required('Required'),
    section_id: Yup.string().required('Required'),
    admission_type: Yup.string().required('Required'),
    roll_number: Yup.string().when('auto_roll_number', {
      is: false,
      then: () => Yup.string().required('Roll number is required when auto-generate is unchecked'),
    }),
    admission_test_marks: Yup.string().test('is-num', 'Marks must be valid number/percentage', (val) => !val || /^[0-9]+(\.[0-9]{1,2})?%?$/.test(val)),
  }),
  // Step 2 - Parent & Address Details
  Yup.object().shape({
    father_name: Yup.string()
      .trim()
      .required('Required')
      .matches(/^[a-zA-Z\s.,'-]+$/, 'Father name must contain only letters'),
    father_mobile: Yup.string()
      .required('Father mobile is required')
      .matches(/^\d{10}$/, 'Mobile number must be exactly 10 digits'),
    father_email: Yup.string().test('is-email', 'Must be a valid email address', (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)),
    father_occupation: Yup.string()
      .trim()
      .nullable()
      .test('is-father-occ', 'Occupation must contain only letters', (val) => !val || /^[a-zA-Z\s.,'-]+$/.test(val)),
    mother_name: Yup.string()
      .trim()
      .test('is-name', 'Mother name must contain only letters', (val) => !val || /^[a-zA-Z\s.,'-]+$/.test(val)),
    mother_mobile: Yup.string().test('is-phone', 'Mobile number must be exactly 10 digits', (val) => !val || /^\d{10}$/.test(val)),
    mother_email: Yup.string().test('is-email', 'Must be a valid email address', (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)),
    mother_occupation: Yup.string()
      .trim()
      .test('is-occ', 'Occupation must contain only letters', (val) => !val || /^[a-zA-Z\s.,'-]+$/.test(val)),
    guardian_name: Yup.string()
      .trim()
      .required('Guardian name is required')
      .matches(/^[a-zA-Z\s.,'-]+$/, 'Guardian name must contain only letters'),
    guardian_relation: Yup.string()
      .trim()
      .required('Guardian relation is required')
      .matches(/^[a-zA-Z\s.,'-]+$/, 'Relation must contain only letters'),
    guardian_mobile: Yup.string()
      .required('Guardian mobile is required')
      .matches(/^\d{10}$/, 'Mobile number must be exactly 10 digits'),
    annual_income: Yup.string().test('is-num', 'Annual income must be a valid number', (val) => !val || /^\d+(\.\d{1,2})?$/.test(val)),
    primary_login: Yup.string().required('Primary login is required'),
    current_address: Yup.string().required('Current address is required'),
    current_city: Yup.string()
      .trim()
      .required('City is required')
      .matches(/^[a-zA-Z\s.,'-]+$/, 'City must contain only letters'),
    current_state: Yup.string()
      .trim()
      .required('State is required')
      .matches(/^[a-zA-Z\s.,'-]+$/, 'State must contain only letters'),
    current_country: Yup.string()
      .trim()
      .required('Country is required')
      .matches(/^[a-zA-Z\s.,'-]+$/, 'Country must contain only letters'),
    current_pincode: Yup.string()
      .required('Pincode is required')
      .matches(/^\d{6}$/, 'Pincode must be exactly 6 digits'),
    permanent_address: Yup.string().when('sameAsCurrent', {
      is: false,
      then: () => Yup.string().required('Permanent address is required'),
    }),
    permanent_city: Yup.string().when('sameAsCurrent', {
      is: false,
      then: () =>
        Yup.string()
          .trim()
          .required('Permanent city is required')
          .matches(/^[a-zA-Z\s.,'-]+$/, 'City must contain only letters'),
    }),
    permanent_state: Yup.string().when('sameAsCurrent', {
      is: false,
      then: () =>
        Yup.string()
          .trim()
          .required('Permanent state is required')
          .matches(/^[a-zA-Z\s.,'-]+$/, 'State must contain only letters'),
    }),
    permanent_country: Yup.string().when('sameAsCurrent', {
      is: false,
      then: () =>
        Yup.string()
          .trim()
          .required('Permanent country is required')
          .matches(/^[a-zA-Z\s.,'-]+$/, 'Country must contain only letters'),
    }),
    permanent_pincode: Yup.string().when('sameAsCurrent', {
      is: false,
      then: () => Yup.string()
        .required('Permanent pincode is required')
        .matches(/^\d{6}$/, 'Pincode must be exactly 6 digits'),
    }),
  }),
  // Step 3 - Documents & Review
  Yup.object().shape({}),
];

export function AddStudentWizard({ initialData = null, isEditMode = false }) {
  const [activeStep, setActiveStep] = useState(0);
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const navigate = useNavigate();
  const { schoolId } = useSchool();
  const { mutate: createStudent, loading: createLoading } = useCreateStudent();
  const { mutate: updateStudent, loading: updateLoading } = useUpdateStudent();

  const loading = createLoading || updateLoading;
  const isLastStep = activeStep === steps.length - 1;

  useEffect(() => {
    async function fetchMasterData() {
      if (!schoolId) return;
      setSessionsLoaded(false);
      try {
        const [resClasses, resSessions] = await Promise.all([
          studentService.getClassesSections(),
          academicSessionService.getSessionsBySchool(schoolId),
        ]);
        setClasses(resClasses.data || []);
        setSessions(Array.isArray(resSessions) ? resSessions : (resSessions.data || []));
      } catch {
        // Silently catch master data fetch errors
      } finally {
        setSessionsLoaded(true);
      }
    }
    fetchMasterData();
  }, [schoolId]);

  const hasActiveSession = sessions.some(s => s.is_current || s.status === 'ACTIVE');

  if (sessionsLoaded && !hasActiveSession) {
    return (
      <WidgetContainer>
        <Box
          sx={{
            py: 6,
            px: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: tokens.colors.primary[50],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2.5,
            }}
          >
            <CalendarX size={36} color={tokens.colors.primary.main} />
          </Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            No Active Academic Session Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mb: 3.5, lineHeight: 1.6 }}>
            There is no active academic session configured for this school. Please create or activate an academic session first before adding a student.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<Plus size={20} />}
            onClick={() => navigate('/academic-sessions')}
            sx={{ borderRadius: tokens.radius.full, px: 4, py: 1.2 }}
          >
            Create Academic Session
          </Button>
        </Box>
      </WidgetContainer>
    );
  }

  const rawEnrollment = initialData?.enrollment || initialData?.student?.enrollment || {};

  const academicSessionId =
    (typeof rawEnrollment.academic_session === 'object' ? rawEnrollment.academic_session?.id : null) ||
    (typeof rawEnrollment.academic_session_id === 'object' ? rawEnrollment.academic_session_id?.id : rawEnrollment.academic_session_id) ||
    '';

  const classId =
    (typeof rawEnrollment.class === 'object' ? rawEnrollment.class?.id : null) ||
    (typeof rawEnrollment.class_id === 'object' ? rawEnrollment.class_id?.id : rawEnrollment.class_id) ||
    '';

  const sectionId =
    (typeof rawEnrollment.section === 'object' ? rawEnrollment.section?.id : null) ||
    (typeof rawEnrollment.section_id === 'object' ? rawEnrollment.section_id?.id : rawEnrollment.section_id) ||
    '';

  const enrollmentValues = {
    ...rawEnrollment,
    academic_session_id: academicSessionId,
    class_id: classId,
    section_id: sectionId,
  };

  const cleanNulls = (obj) => {
    if (!obj || typeof obj !== 'object') return {};
    const res = {};
    for (const [k, v] of Object.entries(obj)) {
      res[k] = v === null ? '' : v;
    }
    return res;
  };

  const mergedInitialValues = {
    ...initialValues,
    ...cleanNulls(initialData?.student),
    sr_number: initialData?.student?.sr_number || initialData?.student?.sr_no || initialData?.sr_number || initialData?.sr_no || '',
    photoPreview: initialData?.student?.photo || '',
    ...cleanNulls(initialData?.parent),
    primary_login: initialData?.parent?.primary_login || initialData?.student?.primary_login || 'FATHER',
    ...cleanNulls(initialData?.address),
    ...cleanNulls(enrollmentValues),
    previous_academics: initialData?.previous_academics || [],
    documents: initialData?.documents || [],
    siblings: Array.isArray(initialData?.siblings)
      ? initialData.siblings.map((s) => ({
          student_id: s.student_id || s.id || '',
          relationship: s.relationship || s.relation || 'BROTHER',
          student_name: s.student_name || s.name || (s.first_name ? `${s.first_name} ${s.last_name || ''}` : '') || '',
          admission_no: s.admission_no || '',
          class_name: s.class?.name || s.class_name || '',
          section_name: s.section?.name || s.section_name || '',
          father_name: s.parent?.father?.name || s.father_name || '',
        }))
      : Array.isArray(initialData?.student?.siblings)
      ? initialData.student.siblings.map((s) => ({
          student_id: s.student_id || s.id || '',
          relationship: s.relationship || s.relation || 'BROTHER',
          student_name: s.student_name || s.name || '',
          admission_no: s.admission_no || '',
          class_name: s.class?.name || s.class_name || '',
          section_name: s.section?.name || s.section_name || '',
          father_name: s.parent?.father?.name || s.father_name || '',
        }))
      : [],
    auto_roll_number: isEditMode ? false : true,
  };

  const handleSubmit = async (values, actions) => {
    if (isLastStep) {
      try {
        const cleanVal = (v) => (v && typeof v === 'string' ? (v.trim() || null) : (v || null));

        const documentList = [];
        const attachedFiles = [];

        const docsSource = Array.isArray(values.documents) && values.documents.length > 0
          ? values.documents
          : (initialData?.documents || []);

        docsSource.forEach((doc) => {
          let docType = '';
          let docNum = '';
          if (typeof doc === 'string') {
            docType = normalizeDocTypeKey(doc);
          } else if (doc && typeof doc === 'object') {
            docType = normalizeDocTypeKey(doc.document_type);
            docNum = doc.document_number || '';
            if (doc.file instanceof File || doc.file instanceof Blob) {
              attachedFiles.push(doc.file);
            }
          }
          if (docType) {
            documentList.push({
              document_type: docType,
              document_number: docNum,
            });
          }
        });

        if (Array.isArray(values.files)) {
          values.files.forEach((file) => {
            if (file instanceof File || file instanceof Blob) {
              attachedFiles.push(file);
            }
          });
        }

        // Transform flat values to exact multipart payload schema
        const payload = {
          student: {
            sr_number: values.sr_number?.trim() || '',
            first_name: values.first_name?.trim() || '',
            middle_name: values.middle_name?.trim() || '',
            last_name: values.last_name?.trim() || '',
            gender: (values.gender || '').toUpperCase(),
            date_of_birth: values.date_of_birth || '',
            blood_group: values.blood_group || '',
            aadhaar_no: values.aadhaar_no || '',
            religion: values.religion || '',
            category: (values.category || '').toUpperCase(),
            caste: values.caste || '',
            nationality: values.nationality || 'Indian',
            height: values.height ? String(values.height) : '',
            weight: values.weight ? String(values.weight) : '',
            is_rte: Boolean(values.is_rte),
          },
          parent: {
            father_name: values.father_name?.trim() || '',
            father_mobile: values.father_mobile?.trim() || '',
            father_email: cleanVal(values.father_email),
            father_occupation: values.father_occupation?.trim() || '',
            mother_name: cleanVal(values.mother_name),
            mother_mobile: cleanVal(values.mother_mobile),
            mother_email: cleanVal(values.mother_email),
            mother_occupation: cleanVal(values.mother_occupation),
            guardian_name: values.guardian_name?.trim() || '',
            guardian_relation: values.guardian_relation?.trim() || '',
            guardian_mobile: values.guardian_mobile?.trim() || '',
            annual_income: values.annual_income ? Number(values.annual_income) || 0 : 0,
            primary_login: (values.primary_login || 'FATHER').toUpperCase(),
          },
          address: {
            current_address: values.current_address?.trim() || '',
            current_city: values.current_city?.trim() || '',
            current_state: values.current_state?.trim() || '',
            current_country: values.current_country?.trim() || '',
            current_pincode: values.current_pincode?.trim() || '',
            permanent_address: values.sameAsCurrent ? (values.current_address?.trim() || '') : (values.permanent_address?.trim() || ''),
            permanent_city: values.sameAsCurrent ? (values.current_city?.trim() || '') : (values.permanent_city?.trim() || ''),
            permanent_state: values.sameAsCurrent ? (values.current_state?.trim() || '') : (values.permanent_state?.trim() || ''),
            permanent_country: values.sameAsCurrent ? (values.current_country?.trim() || '') : (values.permanent_country?.trim() || ''),
            permanent_pincode: values.sameAsCurrent ? (values.current_pincode?.trim() || '') : (values.permanent_pincode?.trim() || ''),
          },
          enrollment: {
            academic_session_id: values.academic_session_id || '',
            class_id: values.class_id || '',
            section_id: values.section_id || '',
            roll_number: String(values.roll_number || ''),
            admission_date: values.admission_date || '',
            admission_type: (values.admission_type || 'NEW').toUpperCase(),
            remarks: values.remarks || '',
          },
          siblings: Array.isArray(values.siblings)
            ? values.siblings
                .filter((s) => s && s.student_id && s.relationship)
                .map((s) => ({
                  student_id: s.student_id,
                  relationship: (s.relationship || 'BROTHER').toUpperCase(),
                }))
            : [],
          previous_academics: Array.isArray(values.previous_academics || initialData?.previous_academics)
            ? (values.previous_academics || initialData?.previous_academics || []).map((item) => ({
              school_name: item?.school_name || '',
              board: item?.board || '',
              medium: item?.medium || '',
              last_class: item?.last_class || '',
              percentage: item?.percentage ? Number(item.percentage) || 0 : 0,
              tc_number: cleanVal(item?.tc_number),
              tc_date: cleanVal(item?.tc_date),
              remarks: cleanVal(item?.remarks),
            }))
            : [],
          documents: documentList,
          photo: values.photoFile || (values.photo instanceof File ? values.photo : null),
          files: attachedFiles,
        };

        if (isEditMode && initialData?.student?.id) {
          await updateStudent(initialData.student.id, payload);
          toast.success('Student updated successfully');
        } else {
          await createStudent(payload);
          toast.success('Student enrolled successfully');
        }
        navigate('/students/list');
      } catch (err) {
        const errorDetail = err.response?.data?.detail;
        let errorMsg = err.response?.data?.message || err.message || 'Failed to submit admission';
        if (Array.isArray(errorDetail)) {
          errorMsg = errorDetail.map((d) => d.msg || d.message).join(' | ');
        } else if (typeof errorDetail === 'string') {
          errorMsg = errorDetail;
        }
        toast.error(errorMsg);
      } finally {
        actions.setSubmitting(false);
      }
    } else {
      // Validate Aadhaar availability before proceeding from Step 0
      const isExistingAadhaar = isEditMode && String(values.aadhaar_no || '').trim() === String(initialData?.student?.aadhaar_no || '').trim();
      if (!isExistingAadhaar && activeStep === 0 && values.aadhaar_no && values.aadhaar_no.trim().length === 12 && schoolId) {
        try {
          const res = await studentsApi.validateAadhaar(schoolId, values.aadhaar_no.trim());
          const data = res?.data || res;
          if (data?.available === false) {
            const msg = data?.message || 'This Aadhaar is already associated with another student. Please enter the correct Aadhaar number for the student.';
            toast.error(msg);
            actions.setFieldError('aadhaar_no', msg);
            actions.setSubmitting(false);
            return;
          }
        } catch (err) {
          const msg = err?.response?.data?.data?.message || err?.response?.data?.message || err?.message;
          if (msg) {
            toast.error(msg);
            actions.setFieldError('aadhaar_no', msg);
            actions.setSubmitting(false);
            return;
          }
        }
      }

      // Validate Section capacity before proceeding from Step 0
      if (activeStep === 0 && values.class_id && values.section_id) {
        const selectedClass = classes.find((c) => c.id === values.class_id);
        const selectedSection = selectedClass?.sections?.find((s) => s.id === values.section_id);
        const isExistingStudentSec = isEditMode && (
          initialData?.enrollment?.section_id === values.section_id || 
          initialData?.student?.section_id === values.section_id ||
          initialData?.section_id === values.section_id
        );

        if (
          !isExistingStudentSec &&
          selectedSection &&
          Number(selectedSection.capacity) > 0 &&
          Number(selectedSection.current_strength) >= Number(selectedSection.capacity)
        ) {
          const availableSecs = (selectedClass?.sections || []).filter(
            (s) => Number(s.capacity) === 0 || Number(s.current_strength) < Number(s.capacity)
          );
          const altMsg = availableSecs.length > 0
            ? ` Recommended available section(s): ${availableSecs.map((s) => `Section ${s.section_name}`).join(', ')}.`
            : ' All sections in this class are at full capacity.';
          const fullMsg = `Section ${selectedSection.section_name} is full (${selectedSection.current_strength}/${selectedSection.capacity} students).${altMsg}`;

          toast.error(fullMsg);
          actions.setFieldError('section_id', `Section ${selectedSection.section_name} is full. Please choose an available section.`);
          actions.setSubmitting(false);
          return;
        }
      }

      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  const renderStepContent = (step, formikProps) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <StepBasicDetails formik={formikProps} isEditMode={isEditMode} initialData={initialData} />
            <Divider />
            <StepAcademic formik={formikProps} isEditMode={isEditMode} initialData={initialData} />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <StepParents formik={formikProps} />
            <Divider />
            <StepAddress formik={formikProps} />
          </Box>
        );
      case 2: {
        const v = formikProps.values;
        const currentSession = sessions.find(s => s.id === v.academic_session_id);
        const sessionName = currentSession?.session_name || v.session_name || v.academic_session_id || 'N/A';

        const currentClass = classes.find(c => c.id === v.class_id);
        const className = currentClass?.class_name || v.className || v.class_id || 'N/A';

        const currentSection = currentClass?.sections?.find(sec => sec.id === v.section_id);
        const sectionName = currentSection?.section_name 
          ? `Section ${currentSection.section_name}${currentSection.capacity ? ` (${currentSection.current_strength ?? 0}/${currentSection.capacity} students)` : ''}` 
          : (v.sectionName || v.section_id || 'N/A');

        const fullName = [v.first_name, v.middle_name, v.last_name].filter(Boolean).join(' ');

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <StepPreviousAcademicsAndDocuments formik={formikProps} isEditMode={isEditMode} />
            <Divider />
            <Box sx={{ py: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 2 }}>
              Review & Confirm All Details
            </Typography>

            <Grid container spacing={2}>
              {/* Basic Details Card */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: tokens.radius.md, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    {(v.photoPreview || v.photo) && (
                      <Avatar
                        src={v.photoPreview || v.photo}
                        sx={{ width: 48, height: 48, boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}
                      />
                    )}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} color="primary">
                        Basic Details
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="text.primary">
                        {fullName || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={1} rowSpacing={0.75}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Gender</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.gender || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Date of Birth</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.date_of_birth || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Blood Group</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.blood_group || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Aadhaar No</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.aadhaar_no || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Nationality</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.nationality || 'INDIAN'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Religion</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.religion || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Category</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.category || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Caste</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.caste || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Height / Weight</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {v.height ? `${v.height} cm` : 'N/A'} / {v.weight ? `${v.weight} kg` : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">RTE Student</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.is_rte ? 'Yes' : 'No'}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Academic Info Card */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: tokens.radius.md, height: '100%' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom sx={{ mb: 1.5 }}>
                    Academic Information
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={1} rowSpacing={0.75}>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Academic Session</Typography>
                      <Typography variant="body2" fontWeight={600}>{sessionName}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Class</Typography>
                      <Typography variant="body2" fontWeight={600}>{className}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Section</Typography>
                      <Typography variant="body2" fontWeight={600}>{sectionName}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Admission Date</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.admission_date || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Admission Type</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.admission_type || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Roll Number</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {v.auto_roll_number ? 'Auto-generated' : (v.roll_number || 'N/A')}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">SR Number</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.sr_number || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Test Marks / (%)</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.admission_test_marks || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Remarks</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.remarks || 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Parent & Guardian Info Card */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: tokens.radius.md, height: '100%' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom sx={{ mb: 1.5 }}>
                    Parent & Guardian Info
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={1} rowSpacing={0.75}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Father's Name</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.father_name || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Father's Mobile</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.father_mobile || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Father's Email</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>{v.father_email || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Father's Occupation</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.father_occupation || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Mother's Name</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.mother_name || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Mother's Mobile</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.mother_mobile || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Mother's Email</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>{v.mother_email || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Mother's Occupation</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.mother_occupation || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Guardian Name</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.guardian_name || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Guardian Relation</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.guardian_relation || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Guardian Mobile</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.guardian_mobile || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Annual Income</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.annual_income ? `₹${v.annual_income}` : 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Primary Login</Typography>
                      <Typography variant="body2" fontWeight={600}>{v.primary_login || 'FATHER'}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Address Details Card */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: tokens.radius.md, height: '100%' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom sx={{ mb: 1.5 }}>
                    Address Details
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block">Current Address</Typography>
                  <Typography variant="body2" fontWeight={600} mb={1.5}>
                    {[v.current_address, v.current_city, v.current_state, v.current_country, v.current_pincode].filter(Boolean).join(', ') || 'N/A'}
                  </Typography>

                  <Typography variant="caption" color="text.secondary" display="block">Permanent Address</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {v.sameAsCurrent
                      ? 'Same as Current Address'
                      : ([v.permanent_address, v.permanent_city, v.permanent_state, v.permanent_country, v.permanent_pincode].filter(Boolean).join(', ') || 'N/A')}
                  </Typography>
                </Paper>
              </Grid>

              {/* Previous Academics & Documents Card */}
              <Grid size={{ xs: 12 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: tokens.radius.md }}>
                  <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom sx={{ mb: 1.5 }}>
                    Academic History & Documents
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: isEditMode ? 6 : 12 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Previous Academic Records ({Array.isArray(v.previous_academics) ? v.previous_academics.length : 0})
                      </Typography>
                      {Array.isArray(v.previous_academics) && v.previous_academics.length > 0 ? (
                        <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {v.previous_academics.map((rec, i) => (
                            <Typography key={i} variant="body2" fontWeight={600}>
                              • {rec.school_name || 'School'} ({rec.board || 'Board'}, {rec.last_class || 'Class'}) - {rec.percentage || 0}%
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" fontWeight={600}>None added</Typography>
                      )}
                    </Grid>
                    {isEditMode && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Submitted Documents ({Array.isArray(v.documents) ? v.documents.length : 0})
                        </Typography>
                        {Array.isArray(v.documents) && v.documents.length > 0 ? (
                          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {v.documents.map((doc, idx) => (
                              <Typography key={idx} variant="body2" fontWeight={600} sx={{ bgcolor: 'success.50', color: 'success.dark', px: 1, py: 0.25, borderRadius: 1, display: 'inline-block' }}>
                                {typeof doc === 'string' ? doc : doc?.document_type || 'Document'}
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" fontWeight={600}>None selected</Typography>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {/* Siblings Card */}
              {Array.isArray(v.siblings) && v.siblings.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: tokens.radius.md }}>
                    <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom sx={{ mb: 1.5 }}>
                      Sibling Information ({v.siblings.length})
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {v.siblings
                        .filter(s => s && s.student_id)
                        .map((s, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 1.5,
                              py: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              bgcolor: 'action.hover',
                              minWidth: 180,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={700} noWrap>
                                {s.student_name || 'Student'}
                              </Typography>
                              {s.admission_no && (
                                <Typography variant="caption" color="text.secondary">
                                  {s.admission_no}
                                  {(s.class_name || s.section_name) && ` · ${s.class_name || ''}${s.section_name ? ' ' + s.section_name : ''}`}
                                </Typography>
                              )}
                            </Box>
                            <Chip
                              label={(s.relationship || '').replace(/_/g, ' ')}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}
                            />
                          </Box>
                        ))}
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
          </Box>
        );
      }
      default:
        return <div>Not Found</div>;
    }
  };

  return (
    <WidgetContainer>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ pt: 3, pb: 5 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Formik
        initialValues={mergedInitialValues}
        validationSchema={validationSchemas[activeStep]}
        enableReinitialize={true}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            <Box sx={{ py: 2 }}>
              {renderStepContent(activeStep, formikProps)}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: `1px solid ${tokens.colors.border}` }}>
              <Button
                disabled={activeStep === 0 || loading}
                onClick={() => setActiveStep((prev) => prev - 1)}
                variant="outlined"
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading || formikProps.isSubmitting}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isLastStep ? (isEditMode ? 'Update Student' : 'Submit Admission') : 'Next Step'}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </WidgetContainer>
  );
}
